import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money } from '@/lib/helpers';
import { Loader2, ScanLine, Camera, CameraOff, X, CheckCircle2 } from 'lucide-react';

const CAM_ID = 'scanmove-camera';

export default function PurchaseScanMove({ open, onOpenChange, purchaseId, garments = [], onSaved }) {
  const { stores } = useApp();
  const [code, setCode] = useState('');
  const [scanned, setScanned] = useState([]); // array of {barcode, product, store_id}
  const [store, setStore] = useState('');       // '' = no change
  const [price, setPrice] = useState('');
  const [cost, setCost] = useState('');
  const [camOn, setCamOn] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const scannerRef = useRef(null);
  const lastCamRef = useRef({ code: '', ts: 0 });
  const audioRef = useRef(null);

  // Short WebAudio beep + haptic. kind: 'ok' (accepted) or 'err' (rejected/duplicate).
  const feedback = (kind) => {
    let pref = { enabled: true, volume: 1 };
    try { const raw = localStorage.getItem('shabz_scan_beep'); if (raw) pref = { ...pref, ...JSON.parse(raw) }; } catch (e) { /* default */ }
    const vol = pref.enabled ? Math.max(0, Math.min(1, Number(pref.volume))) : 0;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && vol > 0) {
        if (!audioRef.current) audioRef.current = new AC();
        const ctx = audioRef.current;
        if (ctx.state === 'suspended') ctx.resume();
        const t0 = ctx.currentTime;
        const tone = (freq, start, dur, type, gain) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = type; o.frequency.value = freq; o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(gain * vol, t0 + start);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
          o.start(t0 + start); o.stop(t0 + start + dur);
        };
        if (kind === 'ok') {
          // louder, crisp two-note "accepted" chirp
          tone(1180, 0, 0.10, 'square', 0.5);
          tone(1650, 0.10, 0.13, 'square', 0.5);
        } else {
          // distinctly different low descending "error" buzzer
          tone(340, 0, 0.16, 'sawtooth', 0.45);
          tone(180, 0.17, 0.30, 'sawtooth', 0.45);
        }
      }
    } catch (e) { /* audio unsupported */ }
    try { if (navigator.vibrate) navigator.vibrate(kind === 'ok' ? 40 : [60, 50, 60]); } catch (e) { /* no haptics */ }
  };

  const map = useMemo(() => {
    const m = new Map();
    for (const g of garments) m.set(String(g.barcode).toUpperCase(), g);
    return m;
  }, [garments]);
  // Always-current set of scanned codes. A ref (not derived state) so the camera callback —
  // which is registered once — and rapid back-to-back scans both see the latest set and can
  // never add the same code twice.
  const scannedRef = useRef(new Set());

  useEffect(() => {
    if (open) { scannedRef.current = new Set(); setScanned([]); setStore(''); setPrice(''); setCost(''); setCode(''); setCamOn(false);
      setTimeout(() => inputRef.current?.focus(), 200); }
  }, [open]);

  const storeName = (id) => stores.find((s) => s.id === id)?.name || '—';

  const addCode = (raw) => {
    const c = String(raw || '').trim().toUpperCase();
    if (!c) return;
    if (scannedRef.current.has(c)) { feedback('err'); toast(`${c} is already scanned`); return; }
    const g = map.get(c);
    if (!g) { feedback('err'); toast.error(`${c} is not part of this purchase`); return; }
    if (g.status !== 'AVAILABLE') { feedback('err'); toast.error(`${c} is ${g.status} — can't move it`); return; }
    scannedRef.current.add(c);           // mark immediately (synchronous) to block repeats
    feedback('ok');
    setScanned((s) => [{ barcode: c, product: g.product, store_id: g.store_id }, ...s]);
  };

  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); addCode(code); setCode(''); } };
  const removeScan = (bc) => { scannedRef.current.delete(bc); setScanned((s) => s.filter((x) => x.barcode !== bc)); };
  const clearAll = () => { scannedRef.current = new Set(); setScanned([]); };

  // ---- camera scanning (html5-qrcode, lazy) ----
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import('html5-qrcode');
        if (cancelled) return;
        const scanner = new Html5Qrcode(CAM_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.EAN_13, Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A, Html5QrcodeSupportedFormats.QR_CODE,
          ],
        });
        scannerRef.current = scanner;
        await scanner.start({ facingMode: 'environment' }, { fps: 10, qrbox: { width: 240, height: 160 } },
          (text) => {
            const now = Date.now();
            if (text === lastCamRef.current.code && now - lastCamRef.current.ts < 1500) return;
            lastCamRef.current = { code: text, ts: now };
            addCode(text);
          }, () => {});
      } catch (e) {
        toast.error('Could not start camera. Use the scan/type box instead.');
        setCamOn(false);
      }
    };
    const stop = async () => {
      const sc = scannerRef.current; scannerRef.current = null;
      if (sc) { try { await sc.stop(); } catch (e) { /* ignore */ } try { await sc.clear(); } catch (e) { /* ignore */ } }
    };
    if (open && camOn) start();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOn, open]);

  const hasChange = !!store || price !== '' || cost !== '';
  const uniformFrom = (getter) => {
    const vals = [...new Set(scanned.map(getter))];
    return vals.length === 1 ? vals[0] : null;
  };

  const apply = async () => {
    setSaving(true);
    try {
      const body = { barcodes: scanned.map((s) => s.barcode) };
      if (store) body.store_id = store;
      if (price !== '') body.price = Number(price);
      if (cost !== '') body.cost = Number(cost);
      const r = await api.post(`/purchases/${purchaseId}/reassign`, body);
      toast.success(`${r.data.data.updated} item(s) updated`);
      setConfirmOpen(false);
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  const fromStore = uniformFrom((s) => s.store_id);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ScanLine className="h-5 w-5 text-primary" /> Scan &amp; Move</DialogTitle>
            <DialogDescription>Scan or type item codes from this purchase, then move them to another store and/or update prices. Separate from Stock Transfer.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <ScanLine className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={onKey}
                placeholder="Scan or type a code, then Enter" className="h-11 rounded-xl pl-9" data-testid="scanmove-input" autoComplete="off" />
            </div>
            <Button type="button" variant={camOn ? 'default' : 'outline'} className="h-11 rounded-xl" onClick={() => setCamOn((v) => !v)} data-testid="scanmove-camera-toggle">
              {camOn ? <CameraOff className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </Button>
          </div>
          {camOn && <div id={CAM_ID} className="mt-1 overflow-hidden rounded-xl border border-border bg-black" data-testid="scanmove-camera" />}

          <div className="rounded-xl border border-border">
            <div className="flex items-center justify-between border-b border-border px-3 py-2 text-sm">
              <span className="font-semibold" data-testid="scanmove-count">{scanned.length} item(s) scanned</span>
              {scanned.length > 0 && <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={clearAll} data-testid="scanmove-clear">Clear</button>}
            </div>
            <div className="max-h-[26vh] overflow-auto">
              {scanned.length === 0 ? (
                <p className="px-3 py-6 text-center text-sm text-muted-foreground">Nothing scanned yet.</p>
              ) : scanned.map((s) => (
                <div key={s.barcode} className="flex items-center justify-between border-b border-border px-3 py-2 last:border-0" data-testid={`scanmove-item-${s.barcode}`}>
                  <div className="min-w-0"><p className="truncate text-sm font-medium">{s.product}</p><p className="font-mono text-xs text-muted-foreground">{s.barcode} · {storeName(s.store_id)}</p></div>
                  <button type="button" onClick={() => removeScan(s.barcode)} className="rounded p-1 text-muted-foreground hover:text-destructive" data-testid={`scanmove-remove-${s.barcode}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 rounded-xl bg-secondary/40 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Changes to apply (each optional)</p>
            <div>
              <Label className="text-xs">Move to store</Label>
              <Select value={store} onValueChange={setStore}>
                <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="scanmove-store"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Sale price (₹)</Label><Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="No change" className="mt-1 h-11 rounded-xl" data-testid="scanmove-price" /></div>
              <div><Label className="text-xs">Purchase price (₹)</Label><Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="No change" className="mt-1 h-11 rounded-xl" data-testid="scanmove-cost" /></div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" className="h-11 flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={saving} data-testid="scanmove-cancel">Cancel</Button>
            <Button className="h-11 flex-1 rounded-xl" disabled={!scanned.length || !hasChange} onClick={() => setConfirmOpen(true)} data-testid="scanmove-review">Review &amp; Apply</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="scanmove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" /> Apply these changes?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1.5 text-sm">
                <p className="font-semibold text-foreground" data-testid="scanmove-confirm-count">{scanned.length} items selected</p>
                {store && <p>Store: <b>{fromStore ? storeName(fromStore) : 'Various'}</b> → <b>{storeName(store)}</b></p>}
                {price !== '' && <p>Sale Price → <b>{money(Number(price))}</b></p>}
                {cost !== '' && <p>Purchase Price → <b>{money(Number(cost))}</b></p>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scanmove-confirm-cancel">Back</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); apply(); }} disabled={saving} data-testid="scanmove-confirm-apply">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Changes'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
